/**
=========================================================
* Material Dashboard 2 React - v2.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

// @mui material components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import { useState, useEffect } from "react";

// Material Dashboard 2 React components
import MDBox from "../../components/MDBox";
import MDTypography from "../../components/MDTypography";

// Material Dashboard 2 React example components
import DashboardLayout from "../../examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "../../examples/Navbars/DashboardNavbar";
import Footer from "../../examples/Footer";
import ReportsBarChart from "../../examples/Charts/BarCharts/ReportsBarChart";
import ReportsLineChart from "../../examples/Charts/LineCharts/ReportsLineChart";
import ComplexStatisticsCard from "../../examples/Cards/StatisticsCards/ComplexStatisticsCard";

// Data
import reportsBarChartData from "./data/reportsBarChartData";
import reportsLineChartData from "./data/reportsLineChartData";

// Dashboard components
import Projects from "./components/Projects";
import OrdersOverview from "./components/OrdersOverview";

// Config
import config from "../../config";

function Solar() {
  const { sales, tasks } = reportsLineChartData;
  
  // State for form inputs and prediction results
  const [formData, setFormData] = useState({
    temperature: '',
    humidity: '',
    cloudCover: '',
    solarRadiation: '',
    dayLength: ''
  });
  
  // State for prediction results - separate for Normal and Advanced modes
  const [normalPredictionResults, setNormalPredictionResults] = useState(null);
  const [advancedPredictionResults, setAdvancedPredictionResults] = useState(null);
  
  // State for loading status
  const [isLoading, setIsLoading] = useState(false);
  
  // State for error message
  const [errorMessage, setErrorMessage] = useState('');
  
  // New state variables for weather data
  const [weatherData, setWeatherData] = useState(null);
  const [weatherLoading, setWeatherLoading] = useState(false);
  const [weatherError, setWeatherError] = useState("");
  const [location, setLocation] = useState("California,USA");
  const [date, setDate] = useState(""); // Default to today
  
  // Function to format numbers with commas
  const formatNumber = (num) => {
    if (num === undefined || num === null || num === "--") return "--";
    return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  };
  
  // Function to fetch weather data from the backend
  const fetchWeatherData = async () => {
    setWeatherLoading(true);
    setWeatherError("");
    
    try {
      // Construct the API URL with query parameters
      let url = `${config.apiBaseUrl}/api/predict-from-weather?location=${encodeURIComponent(location)}`;
      if (date) {
        url += `&date=${encodeURIComponent(date)}`;
      }
      
      const response = await fetch(url);
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to fetch weather data');
      }
      
      const data = await response.json();
      console.log('Weather data:', data);
      
      // Update weather data state
      setWeatherData(data);
      
      console.log('API response data:', data);
      
      // Update Normal Mode prediction results
      setNormalPredictionResults({
        energy: data.energy,
        co2_avoided: data.co2_avoided,
        barrels_saved: data.barrels_saved,
        dollars_saved: data.dollars_saved,
        coal_saved: data.coal_saved,
        natural_gas_saved: data.natural_gas_saved,
        uranium_saved: data.uranium_saved
      });
      console.log('Normal Mode prediction results:', normalPredictionResults);
      
    } catch (error) {
      console.error('Error fetching weather data:', error);
      setWeatherError(error.message);
    } finally {
      setWeatherLoading(false);
    }
  };
  
  // Load weather data when component mounts or location/date changes
  useEffect(() => {
    fetchWeatherData();
  }, [location, date]);
  
  // Monitor normalPredictionResults changes
  useEffect(() => {
    if (normalPredictionResults) {
      console.log('normalPredictionResults updated:', normalPredictionResults);
    }
  }, [normalPredictionResults]);
  
  // Monitor advancedPredictionResults changes
  useEffect(() => {
    if (advancedPredictionResults) {
      console.log('advancedPredictionResults updated:', advancedPredictionResults);
    }
  }, [advancedPredictionResults]);
  
  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };
  
  const handleLocationChange = (e) => {
    setLocation(e.target.value);
  };
  
  const handleDateChange = (e) => {
    setDate(e.target.value);
  };
  
  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setErrorMessage('');
    
    try {
      // Convert string values to numbers
      const numericData = {
        temperature: parseFloat(formData.temperature),
        humidity: parseFloat(formData.humidity),
        cloudCover: parseFloat(formData.cloudCover),
        solarRadiation: parseFloat(formData.solarRadiation),
        dayLength: parseFloat(formData.dayLength)
      };
      
      // Validate inputs
      if (Object.values(numericData).some(isNaN)) {
        throw new Error('All fields must be valid numbers');
      }
      
      console.log('Sending data to backend:', numericData);
      
      // Send data to backend API
      const response = await fetch(`${config.apiBaseUrl}/api/predict-solar`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(numericData),
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to get prediction');
      }
      
      const results = await response.json();
      console.log('Prediction results:', results);
      
      // Update Advanced Mode prediction results state
      setAdvancedPredictionResults(results);
      
      // Update the statistics cards with the new values
      // This will be handled by the useEffect below
      
    } catch (error) {
      console.error('Error:', error);
      setErrorMessage(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        {/* Normal Mode Title */}
        <MDBox mt={4.5} mb={2}>
          <MDTypography variant="h4" fontWeight="medium" color="info" textTransform="uppercase" textAlign="left">
            NORMAL MODE: CALIFORNIA WEATHER TODAY
          </MDTypography>
        </MDBox>
        
        {/* Current Weather Data Display */}
        <MDBox mb={3}>
          <Card>
            <MDBox p={3}>
              {weatherLoading && (
                <MDBox textAlign="center" mb={2}>
                  <MDTypography variant="h6" fontWeight="medium">
                    Loading California weather data...
                  </MDTypography>
                </MDBox>
              )}
              
              {weatherError && (
                <MDBox textAlign="center" mb={2}>
                  <MDTypography variant="body2" color="error">
                    {weatherError}
                  </MDTypography>
                  <MDTypography variant="body2" color="text">
                    Note: You need to set the VISUAL_CROSSING_API_KEY environment variable in the backend.
                  </MDTypography>
                  <Button 
                    variant="contained" 
                    color="primary" 
                    onClick={fetchWeatherData}
                    sx={{ mt: 2 }}
                    disabled={weatherLoading}
                  >
                    <MDTypography variant="button" fontWeight="bold" color="white">
                      Retry
                    </MDTypography>
                  </Button>
                </MDBox>
              )}
              
              {weatherData && !weatherError && (
                <>
                  <MDBox textAlign="center" mb={3}>
                    <MDTypography variant="h6" fontWeight="medium">
                      Current Weather for {weatherData.location}
                    </MDTypography>
                    <MDTypography variant="body2" color="text">
                      Date: {weatherData.date} • Sunrise: {weatherData.sunrise} • Sunset: {weatherData.sunset}
                    </MDTypography>
                  </MDBox>
                  
                  <Grid container spacing={3}>
                    {/* Temperature */}
                    <Grid item xs={12} sm={6} md={2.4}>
                      <MDBox textAlign="center" p={2} sx={{ border: '1px solid #eee', borderRadius: '8px' }}>
                        <MDTypography variant="button" color="text" fontWeight="medium">
                          Temperature
                        </MDTypography>
                        <MDTypography variant="h5" fontWeight="medium" mt={1}>
                          {weatherData.temperature}°F
                        </MDTypography>
                      </MDBox>
                    </Grid>
                    
                    {/* Humidity */}
                    <Grid item xs={12} sm={6} md={2.4}>
                      <MDBox textAlign="center" p={2} sx={{ border: '1px solid #eee', borderRadius: '8px' }}>
                        <MDTypography variant="button" color="text" fontWeight="medium">
                          Humidity
                        </MDTypography>
                        <MDTypography variant="h5" fontWeight="medium" mt={1}>
                          {weatherData.humidity}%
                        </MDTypography>
                      </MDBox>
                    </Grid>
                    
                    {/* Cloud Cover */}
                    <Grid item xs={12} sm={6} md={2.4}>
                      <MDBox textAlign="center" p={2} sx={{ border: '1px solid #eee', borderRadius: '8px' }}>
                        <MDTypography variant="button" color="text" fontWeight="medium">
                          Cloud Cover
                        </MDTypography>
                        <MDTypography variant="h5" fontWeight="medium" mt={1}>
                          {weatherData.cloudCover}%
                        </MDTypography>
                      </MDBox>
                    </Grid>
                    
                    {/* Solar Radiation */}
                    <Grid item xs={12} sm={6} md={2.4}>
                      <MDBox textAlign="center" p={2} sx={{ border: '1px solid #eee', borderRadius: '8px' }}>
                        <MDTypography variant="button" color="text" fontWeight="medium">
                          Solar Radiation
                        </MDTypography>
                        <MDTypography variant="h5" fontWeight="medium" mt={1}>
                          {weatherData.solarRadiation} W/m²
                        </MDTypography>
                      </MDBox>
                    </Grid>
                    
                    {/* Day Length */}
                    <Grid item xs={12} sm={6} md={2.4}>
                      <MDBox textAlign="center" p={2} sx={{ border: '1px solid #eee', borderRadius: '8px' }}>
                        <MDTypography variant="button" color="text" fontWeight="medium">
                          Day Length
                        </MDTypography>
                        <MDTypography variant="h5" fontWeight="medium" mt={1}>
                          {weatherData.dayLength.toFixed(2)} hours
                        </MDTypography>
                      </MDBox>
                    </Grid>
                  </Grid>
                  
                  <MDBox textAlign="center" mt={3}>
                    <MDTypography variant="body1" color="text" fontWeight="medium">
                      These current California weather values are used to predict solar energy production below
                    </MDTypography>
                    <Button 
                      variant="outlined" 
                      color="info" 
                      onClick={fetchWeatherData}
                      sx={{
                        mt: 2,
                        backgroundColor: '#1976d2',
                        '&:hover': {
                          backgroundColor: '#1565c0', // Darker blue on hover
                          borderColor: '#1565c0'      // Optional: to match the background
                        }
                      }}
                      disabled={weatherLoading}
                    >
                      <MDTypography variant="button" fontWeight="medium" color="white">
                        {weatherLoading ? "Refreshing..." : "Refresh Weather Data"}
                      </MDTypography>
                    </Button>
                  </MDBox>
                </>
              )}
            </MDBox>
          </Card>
        </MDBox>

        {/* Prediction Results Title */}
        <MDBox mt={2} mb={2}>
          <MDTypography variant="h5" fontWeight="medium" color="dark" textAlign="center">
            Solar Energy Production Prediction Based on Current California Weather
          </MDTypography>
        </MDBox>

        {/* Prediction Statistics Cards */}
        <Grid container spacing={3} marginTop={5}>
          <Grid item xs={12} md={6} lg={3}>
            <MDBox mb={1.5}>
              <ComplexStatisticsCard
                color="dark"
                icon="solar_power"
                title="Solar Energy Produced (MWh)"
                count={normalPredictionResults ? formatNumber(normalPredictionResults.energy) : "--"}
                percentage={{
                  color: "success",
                  amount: "",
                  label: "",
                }}
              />
            </MDBox>
          </Grid>
          <Grid item xs={12} md={6} lg={3}>
            <MDBox mb={1.5}>
              <ComplexStatisticsCard
                color="success"
                icon="co2"
                title="CO2 Emissions Avoided (Tons)"
                count={normalPredictionResults ? formatNumber(normalPredictionResults.co2_avoided) : "--"}
                percentage={{
                  color: "success",
                  amount: "",
                  label: "",
                }}
              />
            </MDBox>
          </Grid>
          <Grid item xs={12} md={6} lg={3}>
            <MDBox mb={1.5}>
              <ComplexStatisticsCard
                color="info"
                icon="oil_barrel"
                title="Barrels of Oil Saved (#)"
                count={normalPredictionResults ? formatNumber(normalPredictionResults.barrels_saved) : "--"}
                percentage={{
                  color: "success",
                  amount: "",
                  label: "",
                }}
              />
            </MDBox>
          </Grid>
          <Grid item xs={12} md={6} lg={3}>
            <MDBox mb={1.5}>
              <ComplexStatisticsCard
                color="primary"
                icon="attach_money"
                title="Dollars Saved in oil ($)"
                count={normalPredictionResults ? formatNumber(normalPredictionResults.dollars_saved) : "--"}
                percentage={{
                  color: "success",
                  amount: "",
                  label: "",
                }}
              />
            </MDBox>
          </Grid>
          <Grid item xs={12} md={6} lg={3}>
            <MDBox mb={1.5}>
              <ComplexStatisticsCard
                color="warning"
                icon="local_fire_department"
                title="Coal Saved (Tons)"
                count={normalPredictionResults ? formatNumber(normalPredictionResults.coal_saved) : "--"}
                percentage={{
                  color: "success",
                  amount: "",
                  label: "",
                }}
              />
            </MDBox>
          </Grid>
          <Grid item xs={12} md={6} lg={3}>
            <MDBox mb={1.5}>
              <ComplexStatisticsCard
                color="error"
                icon="whatshot"
                title="Natural Gas Saved (cu. ft)"
                count={normalPredictionResults ? formatNumber(normalPredictionResults.natural_gas_saved) : "--"}
                percentage={{
                  color: "success",
                  amount: "",
                  label: "",
                }}
              />
            </MDBox>
          </Grid>
          <Grid item xs={12} md={6} lg={3}>
            <MDBox mb={1.5}>
              <ComplexStatisticsCard
                color="dark"
                icon="radioactive"
                title="Uranium Saved (lbs)"
                count={normalPredictionResults ? formatNumber(normalPredictionResults.uranium_saved) : "--"}
                percentage={{
                  color: "success",
                  amount: "",
                  label: "",
                }}
              />
            </MDBox>
          </Grid>
        </Grid>
        
        {/* Error message display */}
        {errorMessage && (
          <MDBox mt={2} mb={2}>
            <MDTypography variant="subtitle2" color="error" fontWeight="medium">
              Error: {errorMessage}
            </MDTypography>
          </MDBox>
        )}

        {/* Divider between Normal Mode and Advanced Mode */}
        <MDBox pt={2} pb={2}>
          <Divider sx={{ my: 2, height: '5px', backgroundColor: '#000' }}>
            <MDTypography variant="button" color="black" fontWeight="bold">
              Normal Mode / Advanced Mode
            </MDTypography>
          </Divider>
        </MDBox>

        {/* Advanced Mode Title */}
        <MDBox mt={2} mb={2}>
          <MDTypography variant="h4" fontWeight="medium" color="info" textTransform="uppercase" textAlign="left">
            ADVANCED MODE
          </MDTypography>
        </MDBox>

        {/* Weather Data Input Form */}
        <Grid container spacing={3} mb={3}>
          <Grid item xs={12}>
            <Card>
              <MDBox p={3}>
                <MDTypography variant="h6" fontWeight="medium" mb={2}>
                  Solar Energy Prediction Input
                </MDTypography>
                <MDTypography variant="body2" color="text" mb={3}>
                  Enter weather data to predict solar energy production
                </MDTypography>
                <MDBox component="form" onSubmit={handleSubmit}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6} lg={4}>
                      <TextField
                        fullWidth
                        label="Temperature (°F)"
                        name="temperature"
                        type="number"
                        value={formData.temperature}
                        onChange={handleInputChange}
                        InputProps={{ inputProps: { min: -50, max: 150, step: "any" } }}
                        margin="normal"
                        helperText="Enter temperature in Fahrenheit"
                      />
                    </Grid>
                    <Grid item xs={12} md={6} lg={4}>
                      <TextField
                        fullWidth
                        label="Humidity (%)"
                        name="humidity"
                        type="number"
                        value={formData.humidity}
                        onChange={handleInputChange}
                        InputProps={{ inputProps: { min: 0, max: 100, step: "any" } }}
                        margin="normal"
                        helperText="Enter humidity percentage"
                      />
                    </Grid>
                    <Grid item xs={12} md={6} lg={4}>
                      <TextField
                        fullWidth
                        label="Cloud Cover (%)"
                        name="cloudCover"
                        type="number"
                        value={formData.cloudCover}
                        onChange={handleInputChange}
                        InputProps={{ inputProps: { min: 0, max: 100, step: "any" } }}
                        margin="normal"
                        helperText="Enter cloud cover percentage"
                      />
                    </Grid>
                    <Grid item xs={12} md={6} lg={4}>
                      <TextField
                        fullWidth
                        label="Solar Radiation (W/m²)"
                        name="solarRadiation"
                        type="number"
                        value={formData.solarRadiation}
                        onChange={handleInputChange}
                        InputProps={{ inputProps: { min: 0, step: "any" } }}
                        margin="normal"
                        helperText="Enter solar radiation in W/m²"
                      />
                    </Grid>
                    <Grid item xs={12} md={6} lg={4}>
                      <TextField
                        fullWidth
                        label="Day Length (hours)"
                        name="dayLength"
                        type="number"
                        value={formData.dayLength}
                        onChange={handleInputChange}
                        InputProps={{ inputProps: { min: 0, max: 24, step: "any" } }}
                        margin="normal"
                        helperText="Enter day length in hours"
                      />
                    </Grid>
                    <Grid item xs={12} md={6} lg={4} sx={{ display: 'flex', alignItems: 'center' }}>
                      <Button 
                        variant="contained" 
                        color="primary" 
                        type="submit"
                        sx={{ mt: 2 }}
                        fullWidth
                        disabled={isLoading}
                      >
                        <MDTypography variant="button" fontWeight="bold" color="white">
                          {isLoading ? "Calculating..." : "Calculate Solar Energy Production"}
                        </MDTypography>
                      </Button>
                    </Grid>
                  </Grid>
                </MDBox>
              </MDBox>
            </Card>
          </Grid>
        </Grid>
        <Grid container spacing={3} marginTop={5}>
        <Grid item xs={12} md={6} lg={3}>
            <MDBox mb={1.5}>
              <ComplexStatisticsCard
                color="dark"
                icon="solar_power"
                title="Solar Energy Produced (MWh)"
                count={advancedPredictionResults ? formatNumber(advancedPredictionResults.energy) : "--"}
                percentage={{
                  color: "success",
                  amount: "",
                  label: "",
                }}
              />
            </MDBox>
          </Grid>
          <Grid item xs={12} md={6} lg={3}>
            <MDBox mb={1.5}>
              <ComplexStatisticsCard
                color="success"
                icon="co2"
                title="CO2 Emissions Avoided (Tons)"
                count={advancedPredictionResults ? formatNumber(advancedPredictionResults.co2_avoided) : "--"}
                percentage={{
                  color: "success",
                  amount: "",
                  label: "",
                }}
              />
            </MDBox>
          </Grid>
          <Grid item xs={12} md={6} lg={3}>
            <MDBox mb={1.5}>
              <ComplexStatisticsCard
                color="info"
                icon="oil_barrel"
                title="Barrels of Oil Saved (#)"
                count={advancedPredictionResults ? formatNumber(advancedPredictionResults.barrels_saved) : "--"}
                percentage={{
                  color: "success",
                  amount: "",
                  label: "",
                }}
              />
            </MDBox>
          </Grid>
          <Grid item xs={12} md={6} lg={3}>
            <MDBox mb={1.5}>
              <ComplexStatisticsCard
                color="primary"
                icon="attach_money"
                title="Dollars Saved in oil ($)"
                count={advancedPredictionResults ? formatNumber(advancedPredictionResults.dollars_saved) : "--"}
                percentage={{
                  color: "success",
                  amount: "",
                  label: "",
                }}
              />
            </MDBox>
          </Grid>
          <Grid item xs={12} md={6} lg={3}>
            <MDBox mb={1.5}>
              <ComplexStatisticsCard
                color="warning"
                icon="local_fire_department"
                title="Coal Saved (Tons)"
                count={advancedPredictionResults ? formatNumber(advancedPredictionResults.coal_saved) : "--"}
                percentage={{
                  color: "success",
                  amount: "",
                  label: "",
                }}
              />
            </MDBox>
          </Grid>
          <Grid item xs={12} md={6} lg={3}>
            <MDBox mb={1.5}>
              <ComplexStatisticsCard
                color="error"
                icon="whatshot"
                title="Natural Gas Saved (cu. ft)"
                count={advancedPredictionResults ? formatNumber(advancedPredictionResults.natural_gas_saved) : "--"}
                percentage={{
                  color: "success",
                  amount: "",
                  label: "",
                }}
              />
            </MDBox>
          </Grid>
          <Grid item xs={12} md={6} lg={3}>
            <MDBox mb={1.5}>
              <ComplexStatisticsCard
                color="dark"
                icon="radioactive"
                title="Uranium Saved (lbs)"
                count={advancedPredictionResults ? formatNumber(advancedPredictionResults.uranium_saved) : "--"}
                percentage={{
                  color: "success",
                  amount: "",
                  label: "",
                }}
              />
            </MDBox>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default Solar;
